import React, { useState, useEffect } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCanAccess, selectCanDo } from '../app/auth';
import { projectsApi } from '../app/projects';
import { missionsApi } from '../app/missions';
import { inspectionApi } from '../app/inspection';
import { layersApi } from '../app/layers';
import { Box, Button, Paper, Typography, TextField, IconButton, Accordion, AccordionSummary, AccordionDetails, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';

const ProjectContentHandler = ({project, missions, layers, setMissions, setLayers, setSnackbar, handleCloseSnackbar}) => {
  const urlParams = useParams();
  const navigate = useNavigate();
  const projectId = urlParams.id;
  const canDo = useSelector(selectCanDo);
  const canAccess = useSelector(selectCanAccess);

  // states to store forms datas
  const [inspectionForm, setInspectionForm] = useState({});
  
  const [projectForm, setProjectForm] = useState({ name: '', projectNumber: '' });
  const [missionForm, setMissionForm] = useState({});
  const [layerForm, setLayerForm] = useState({ name: '' });
  const [newMission, setNewMission] = useState({ name: '', projectId: projectId });
  const [expandedLayerId, setExpandedLayerId] = useState(null);
  const [colormapForm, setColormapForm] = useState({});

  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null});

  useEffect(() => {
    setProjectForm({ name: project.name, projectNumber: project.projectNumber });
  }, [project])

  useEffect(() => {
    if (!layers) return

    const cmapInit = {};
    layers.forEach(layer => {
      cmapInit[layer.id] = {
        minValue: layer.colorBar?.min?.value ?? '',
        minUnit: layer.colorBar?.min?.unit ?? '',
        maxValue: layer.colorBar?.max?.value ?? '',
        maxUnit: layer.colorBar?.max?.unit ?? '',
      };
    });
    setColormapForm(prev => ({ ...prev, ...cmapInit }));
  }, [layers])

  const ConfirmDialog = ({ open, title, message, onClose, onConfirm }) => (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => { onConfirm(); onClose(); }} color="error">Confirm</Button>
      </DialogActions>
    </Dialog>
  );

  // handling inputs on change events
  const handleProjectChange = (e) => {
    setProjectForm(p => ({ ...p, [e.target.name]: e.target.value }));
  }
  const handleMissionChange = (e, missionId) => {
    const { name, value } = e.target;
    setMissionForm((prev) => ({
      ...prev,
      [missionId]: {
        ...prev[missionId],
        [name]: value,
      },
    }));
  };
  
  const handleInspectionChange = (e, inspectionId) => {
    const { name, value } = e.target;
    setInspectionForm((prev) => ({
      ...prev,
      [inspectionId]: {
        ...(prev[inspectionId] || {}),
        [name]: value,
      },
    }));
  };
  const handleLayerChange = (e) => {
    const layerId = e.target.name;
    const value = e.target.value;
    setLayerForm(p => ({ ...p, [layerId]: value }));
  }

  // mission
  const handleCreateMission = e => {
    e.preventDefault();
    if (!newMission.name) {
      setSnackbar({ open: true, message: 'Mission name required', severity: 'error' });
      return;
    }
    missionsApi.create(newMission, 'id,name', (res) => {
      if (!res) {
        setSnackbar({ open: true, message: 'Error while creating mission', severity: 'error' });
      } else {
        setMissions(prev => [...prev, res]);
        setNewMission({ name: '', projectId });
        setSnackbar({ open: true, message: 'Mission created', severity: 'success' });
      }
    });
  };
  const handleDeleteMission = (id) => {
    setConfirmDialog({
      open: true,
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this Mission ?',
      onConfirm: () => {
        missionsApi.delete({id}, (res) => {
          if (!res) {
            setSnackbar({ open: true, message: 'Error while deleting mission', severity: 'error' });
          } else {
            setMissions(prev => prev.filter(m => m.id !== id));
            setSnackbar({ open: true, message: 'Mission deleted', severity: 'info' });
          }
        });
      },
    });
  };

  // inspection
  const handleCreateInspection = (e, missionId) => {
    e.preventDefault();
    const name = inspectionForm[missionId]?.name?.trim();
    if (!name) {
      setSnackbar({ open: true, message: 'Nom requis', severity: 'error' });
      return;
    }
    inspectionApi.create({ name, missionId }, 'id,name,missionId', res => {
      if (!res) {
        setSnackbar({ open: true, message: 'Error while creating inspection', severity: 'error' });
      } else {
        setInspectionForm(prev => ({ ...prev, [missionId]: { name: '' } }));
        setSnackbar({ open: true, message: 'Inspection created, please refresh the page to see it', severity: 'success' });
      }
    });
  };

  const handleDeleteInspection = (id) => {
    setConfirmDialog({
      open: true,
      title: 'Confirm deletion',
      message: 'Are you sure you want to delete this inspection ?',
      onConfirm: () => {
        inspectionApi.delete(id, (res) => {
          if (!res) {
            setSnackbar({ open: true, message: 'Error while deleting inspection', severity: 'error' });
          } else {
            setSnackbar({ open: true, message: 'Inspection deleted', severity: 'info' });
          }
        });
      },
    });
  };
  

  // layer
  const handleSaveLayer = (layerId) => {
    const newName = layerForm[layerId]?.trim();
    if (!newName) {
      setSnackbar({ open: true, message: 'Layer name is required', severity: 'error' });
      return;
    }
  
    layersApi.update({ id: layerId, name: newName }, 'id,name', (res) => {
      if (!res) {
        setSnackbar({ open: true, message: 'Error while updating layer', severity: 'error' });
      } else {
        setLayers(prev => prev.map(l => (l.id === layerId ? { ...l, name: newName } : l)));
        setSnackbar({ open: true, message: 'Layer updated', severity: 'success' });
      }
    });
  };
  const handleDeleteLayer = (id) => {
    setConfirmDialog({
      open: true,
      title: 'Confirm deletion',
      message: 'Are you sure you want to delete this layer ?',
      onConfirm: () => {
        layersApi.delete({ id }, (res) => {
          if (!res) {
            setSnackbar({ open: true, message: 'Error while deleting this layer', severity: 'error' });
          } else {
            setLayers(prev => prev.filter(l => l.id !== id));
            setSnackbar({ open: true, message: 'Layer deleted', severity: 'info' });
          }
        });
      },
    });
  };

  const handleNewMissionChange = (e) => {
    setNewMission((prev) => ({ ...prev, name: e.target.value }))
  }

  const handleAddColormap = (layerId) => {
    const data = colormapForm[layerId];
  
    // fields cant be empty
    if (
      !data ||
      data.minValue === '' ||
      data.maxValue === '' ||
      data.minUnit.trim() === '' ||
      data.maxUnit.trim() === ''
    ) {
      setSnackbar({
        open: true,
        message: 'Please fill all colormap fields',
        severity: 'warning',
      });
      return;
    }
  
    const { layerType } = data;
    let url;
  
    // handling url value from type field in form
    if (layerType === 'infrared') {
      url = 'https://orthophoto-viewer-backend.herokuapp.com/proxy/sites-orthophotos/colormaps/echelle_thermique.png';
    } else if (layerType === 'geometry') {
      url = 'https://orthophoto-viewer-backend.herokuapp.com/proxy/sites-orthophotos/colormaps/echelle_geometrie.png';
    } else {
      setSnackbar({
        open: true,
        message: 'Unknown layer type. Please select a valid colormap type.',
        severity: 'warning',
      });
      return;
    }
  
    layersApi.update(
      {
        id: layerId,
        colorBar: {
          url,
          min: { value: parseFloat(data.minValue), unit: data.minUnit },
          max: { value: parseFloat(data.maxValue), unit: data.maxUnit },
        },
      },
      'id,colorBar',
      (res) => {
        // handling update error
        if (!res) {
          setSnackbar({
            open: true,
            message: 'Failed to update colormap',
            severity: 'error',
          });
          return;
        }
        setLayers((prev) =>
          prev.map((layer) =>
            layer.id === layerId
              ? {
                  ...layer,
                  colorBar: res.colorBar,
                }
              : layer
          )
        );
  
        setSnackbar({
          open: true,
          message: 'Colormap updated successfully',
          severity: 'success',
        });

        setExpandedLayerId(null);
      }
    );
  };

  // used for updating missons and inspections
  const handleSubmit = (e, entity, id = null) => {
    e.preventDefault();
    const cb = (res) => {
      if (!res) setSnackbar({ open: true, message: `Error ${entity}`, severity: 'error' });
      else setSnackbar({ open: true, message: `${entity} updated`, severity: 'success' });
    };
    if (entity === 'project') {
      projectsApi.update({ ...projectForm, id: projectId }, 'id', cb);
    } else if (entity === 'mission') {
      missionsApi.update({ id, ...missionForm[id] }, 'id', cb);
    } else if (entity === 'inspection') {
      const data = { ...inspectionForm[id], id };
      inspectionApi.update(data, cb);
    }
  };
  
  if (!project) return null;

  return (
    <>
      {!canAccess("ProjectEditPage", { projectId }) && <Navigate to="/" replace />}

      <Box sx={{ maxWidth: 1200, margin: '2rem auto', padding: '1rem', color: 'white' }}>
        {/* Projet */}
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          {/* Informations projet */}
          <Typography variant="h6" gutterBottom color="white">Informations</Typography>

          <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#2a2a2a', borderRadius: 2 }}>
            <form onSubmit={(e) => handleSubmit(e, 'project')}>
              <TextField
                name="name"
                label="Nom"
                value={projectForm.name}
                onChange={handleProjectChange}
                fullWidth
                margin="normal"
                size="small"
              />
              <TextField
                name="projectNumber"
                label="Numéro d'affaire"
                value={projectForm.projectNumber || ''}
                onChange={handleProjectChange}
                fullWidth
                margin="normal"
                size="small"
              />
              <Button variant="contained" type="submit" sx={{ mt: 2 }}>
                Save
              </Button>
            </form>
          </Paper>
        </Paper>


        {/* Missions */}
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Missions</Typography>

          {/* new mission */}
          {canDo('createMission', {projectId}) && (
            <Box mb={3}>
              <Typography variant="subtitle1" mb={1}>Add new mission</Typography>
              <form onSubmit={handleCreateMission}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Mission name"
                  name="name"
                  value={newMission.name}
                  onChange={handleNewMissionChange}
                  size="small"
                />
                <Button variant="contained" type="submit" sx={{ mt: 1 }}>
                  Create
                </Button>
              </form>
            </Box>
          )}

          {/* Mission list */}
          {missions.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No mission for this project.
            </Typography>
          )}

          <Typography variant="subtitle1" mb={1}>Mission List :</Typography>
          {missions.map((mission) => (
            <Paper
              key={mission.id}
              elevation={1}
              sx={{ p: 2, mb: 3, backgroundColor: '#222', borderRadius: 2 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {mission.name}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteMission(mission.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Mission edit */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit(e, 'mission', mission.id);
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    size="small"
                    label="New name"
                    name="name"
                    value={missionForm.name}
                    onChange={(e) => {
                      handleMissionChange(e, mission.id)
                    }}
                    fullWidth
                    sx={{ flexGrow: 1 }}
                  />
                  <Button size="small" variant="outlined" type="submit">
                    Save
                  </Button>
                </Box>
              </form>

              <Box component="form" onSubmit={(e) => handleCreateInspection(e, mission.id)} sx={{ mt: 2 }}>
                <TextField
                  label="Add a new inspection"
                  size="small"
                  name="name"
                  value={inspectionForm[mission.id]?.name}
                  onChange={(e) => {handleInspectionChange(e, mission.id)}}
                  sx={{ mr: 1 }}
                />
                <Button type="submit" variant="outlined" size="small">
                  Add
                </Button>
              </Box>
              {/* Inspections en accordéon */}
              <Typography variant="subtitle2" sx={{ mt:2, mb:1 }}>
                Inspections :
              </Typography>
              {mission.inspections && mission.inspections.length > 0 ? (
                <Box mt={2}>
                  {mission.inspections.map((inspection) => (
                    <Accordion key={inspection.id} sx={{ backgroundColor: '#333', color: 'white' }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                        aria-controls={`inspection-content-${inspection.id}`}
                        id={`inspection-header-${inspection.id}`}
                      >
                        <Typography sx={{ flexGrow: 1 }}>{inspection.name}</Typography>
                        {inspection.views && inspection.views.length > 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              const viewId = inspection.views[0].id;
                              navigate(`/view_editor?viewId=${viewId}&inspectionId=${inspection.id}`);
                            }}
                            sx={{ mr: 1 }}
                          >
                            View
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteInspection(inspection.id);
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </AccordionSummary>
                      <AccordionDetails sx={{ backgroundColor: '#222' }}>
                        <Box component="form" onSubmit={(e) => handleSubmit(e, 'inspection', inspection.id)} sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Edit inspection</Typography>

                          <TextField
                            label="Name"
                            name="name"
                            size="small"
                            fullWidth
                            value={inspectionForm[inspection.id]?.name ?? inspection.name}
                            onChange={(e) => handleInspectionChange(e, inspection.id)}
                            sx={{ mb: 2 }}
                          />

                          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel id={`mission-select-label-${inspection.id}`}>Mission</InputLabel>
                            <Select
                              labelId={`mission-select-label-${inspection.id}`}
                              name="missionId"
                              value={
                                inspectionForm[inspection.id]?.missionId !== undefined ?
                                  inspectionForm[inspection.id].missionId.toString() :
                                  inspection.missionId}
                              label="Mission"
                              onChange={(e) => handleInspectionChange(e, inspection.id)}
                            >
                              {missions.map((m) => (
                                <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <Button type="submit" variant="contained" size="small">
                            Save inspection
                          </Button>
                        </Box>

                        {/* Liste des layers */}
                        <Typography variant="subtitle2" sx={{ mt:1, mb:1 }}>
                          Layers :
                        </Typography>
                        {layers.filter(layer => layer.inspectionId === inspection.id).length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No layer for this inspection.
                          </Typography>
                        ) : (
                          layers
                            .filter(layer => layer.inspectionId === inspection.id)
                            .map(layer => (
                              <Box
                                key={layer.id}
                                sx={{
                                  backgroundColor: '#2a2a2a',
                                  borderRadius: 1,
                                  p: 2,
                                  mb: 2,
                                }}
                              >
                                {/* Ligne principale : nom + actions */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <TextField
                                    size="small"
                                    name={layer.id}
                                    value={layerForm[layer.id] ?? layer.name}
                                    onChange={handleLayerChange}
                                    sx={{ flexGrow: 1 }}
                                  />

                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleSaveLayer(layer.id)}
                                  >
                                    Save
                                  </Button>

                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteLayer(layer.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>

                                  {/* Colormap button only for orthophoto layers */}
                                  {layer.type === 'orthophoto' && (
                                    <>
                                      <Button
                                        size="small"
                                        variant={layer.colorBar ? 'outlined' : 'text'}
                                        color={layer.colorBar ? 'success' : 'primary'}
                                        onClick={() =>
                                          setExpandedLayerId(prev => (prev === layer.id ? null : layer.id))
                                        }
                                      >
                                        {expandedLayerId === layer.id
                                          ? 'Cancel colormap'
                                          : layer.colorBar
                                          ? 'Edit colormap'
                                          : 'Add colormap'}
                                      </Button>

                                      {layer.colorBar?.min?.value != null &&
                                        layer.colorBar?.max?.value != null && (
                                          <Typography variant="caption" sx={{ color: 'limegreen' }}>
                                            ✓ Colormap set
                                          </Typography>
                                        )}
                                    </>
                                  )}
                                </Box>

                                {/* Formulaire colormap */}
                                {layer.type === 'orthophoto' && expandedLayerId === layer.id && (
                                  <Box
                                    sx={{
                                      mt: 2,
                                      p: 2,
                                      backgroundColor: '#444',
                                      borderRadius: 1,
                                    }}
                                  >
                                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                                      <InputLabel>Layer type</InputLabel>
                                      <Select
                                        value={colormapForm[layer.id]?.layerType || ''}
                                        label="Layer type"
                                        onChange={e =>
                                          setColormapForm(prev => ({
                                            ...prev,
                                            [layer.id]: {
                                              ...prev[layer.id],
                                              layerType: e.target.value,
                                            },
                                          }))
                                        }
                                      >
                                        <MenuItem value="infrared">infrared</MenuItem>
                                        <MenuItem value="geometry">geometry</MenuItem>
                                      </Select>
                                    </FormControl>
                                    <Box display="flex" gap={1} mb={1}>
                                      <TextField
                                        size="small"
                                        label="Min value"
                                        type="number"
                                        value={colormapForm[layer.id]?.minValue || ''}
                                        onChange={e =>
                                          setColormapForm(prev => ({
                                            ...prev,
                                            [layer.id]: {
                                              ...prev[layer.id],
                                              minValue: e.target.value,
                                            },
                                          }))
                                        }
                                      />
                                      <TextField
                                        size="small"
                                        label="Min unit"
                                        value={colormapForm[layer.id]?.minUnit || ''}
                                        onChange={e =>
                                          setColormapForm(prev => ({
                                            ...prev,
                                            [layer.id]: {
                                              ...prev[layer.id],
                                              minUnit: e.target.value,
                                            },
                                          }))
                                        }
                                      />
                                    </Box>

                                    <Box display="flex" gap={1} mb={1}>
                                      <TextField
                                        size="small"
                                        label="Max value"
                                        type="number"
                                        value={colormapForm[layer.id]?.maxValue || ''}
                                        onChange={e =>
                                          setColormapForm(prev => ({
                                            ...prev,
                                            [layer.id]: {
                                              ...prev[layer.id],
                                              maxValue: e.target.value,
                                            },
                                          }))
                                        }
                                      />
                                      <TextField
                                        size="small"
                                        label="Max unit"
                                        value={colormapForm[layer.id]?.maxUnit || ''}
                                        onChange={e =>
                                          setColormapForm(prev => ({
                                            ...prev,
                                            [layer.id]: {
                                              ...prev[layer.id],
                                              maxUnit: e.target.value,
                                            },
                                          }))
                                        }
                                      />
                                    </Box>

                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => handleAddColormap(layer.id)}
                                    >
                                      Add colormap
                                    </Button>
                                  </Box>
                                )}
                              </Box>

                          ))
                        )}

                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 2 }}>
                  No inspection for this mission.
                </Typography>
              )}
            </Paper>
          ))}
        </Paper>
        <ConfirmDialog
          open={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
          onConfirm={() => {
            confirmDialog.onConfirm?.();
            setConfirmDialog(prev => ({ ...prev, open: false }));
          }}
        />
      </Box>
    </>
  );
};

export default ProjectContentHandler;
